// AI Generated Test
import { Component, Input, OnInit } from '@angular/core';
import { TranslateService } from '@client/app/services/translate/translate.service';
import { SessionService } from '@monsido/core/session/session.service';
import { ChangesHistoryService } from '@client/app/pages/global/changes-history/services/changes-history/changes-history.service';
import { ActiveFeatureService } from '@monsido/services/active-feature/active-feature.service';
import { ComplianceAccessibilityService } from '@client/app/blocks/service/compliance/compliance-accessibility/compliance-accessibility.service';
import { PipeService } from '@client/app/filters/pipe-transformation.service';
import { AccessibilityRepoService } from '@client/app/services/api/accessibility-repo/accessibility-repo.service';
import { AccessibilityChecksRepoService } from '@client/app/services/api/accessibility/accessibility-checks-repo/accessibility-checks-repo.service';
import { AccessibilityDoughnutChartService } from '@client/app/blocks/service/charts/accessibility-doughnut-chart.service';

@Component({
    selector: 'domain-dashboard-accessibility',
    standalone: false,
    template: `
        <div class="card panel-monsido-card max-height d-flex flex-column">
            <a class="p-0" [uiSref]="'base.customer.domain.accessibility.summary'" [attr.aria-label]="'Accessibility' | translate">
                <mon-panel-header [pHeader]="'Accessibility' | translate">
                    <div class="col-sm-48 inner-none text-right">
                        <div class="btn btn-link">
                            <span class="pl-1 fas fa-chevron-right" aria-hidden="true"></span>
                        </div>
                    </div>
                </mon-panel-header>
            </a>
            <div class="card-body flex-grow-1">
                <mon-spinner [loading]="loading">
                    <div class="row my-3">
                        <div class="offset-4 col-18">
                            <mon-chart-doughnut
                                    [labels]="labels"
                                    [data]="data"
                                    [options]="options"
                                    [colors]="colors">
                            </mon-chart-doughnut>
                        </div>
                        <div class="col-26">
                            <div class="row">
                                <div class="vcenter col-48 mb-4">
                                    <notification-change title="{{ 'Failing accessibility checks' | translate }}"
                                                         [digit]="summary.checks_with_issues"
                                                         miniDigits=""
                                                         link="base.customer.domain.accessibility.summary"
                                                         unit="">
                                    </notification-change>
                                </div>
                                <div class="col-48 acc-issue-by-type pl-2">
                                    <ul class="list-inline ml-3 mb-0">
                                        <ng-container *ngFor="let accAbbr of accessibilityAbbreviations">
                                            <li *ngIf="accAbbr.guidelineId | accessibilityHasGuidelineId"
                                                [triggers]="'focus hover'"
                                                [ngbTooltip]="accAbbr.tooltipTxt">
                                                <a [uiSref]="'base.customer.domain.accessibility.issues'"
                                                   [uiParams]="{abbr: accAbbr.abbr}"
                                                   class="text-black">
                                                    <div [ngClass]="accAbbr.iconClass" class="inline-block" aria-hidden="true">
                                                    </div>
                                                    <span class="sr-only">{{accAbbr.abbr}}</span>
                                                    <span class="inner-l-smurf">
                                                        {{getChecksCountByAbbreviation(accAbbr.abbr) | monNumber}}
                                                    </span>
                                                </a>
                                            </li>
                                        </ng-container>
                                    </ul>
                                </div>
                            </div>
                        </div>
                    </div>
                </mon-spinner>
            </div>
            <div class="card-footer bg-white text-right" *ngIf="showHistory">
                <a class="btn-link" [uiSref]="'base.customer.domain.changesHistory'" [uiParams]="historyCenterParameters" translate>
                    Show history
                </a>
            </div>
        </div>
    `,
})
export class DomainDashboardAccessibilityComponent implements OnInit {
    @Input() historyData: any;

    loading = true;
    labels: string[] = ['', ''];
    data: any[] = [];
    colors: any[] = [];
    historyCenterParameters: any = {};
    showHistory: boolean = false;
    domain: any;
    accessibilityAbbreviations: any[] = [];
    summary: any = {};
    checks: any;
    options: any;

    constructor (
        private accessibilityRepoService: AccessibilityRepoService,
        private accessibilityChecksRepoService: AccessibilityChecksRepoService,
        private accessibilityDoughnutChartService: AccessibilityDoughnutChartService,
        private translateService: TranslateService,
        private sessionService: SessionService,
        private changesHistoryService: ChangesHistoryService,
        private activeFeatureService: ActiveFeatureService,
        private complianceAccessibilityService: ComplianceAccessibilityService,
        private pipeService: PipeService,
    ) {}

    ngOnInit (): void {
        this.activate();
    }

    private activate (): void {
        this.loading = true;
        this.labels = ['', ''];
        this.data = [];
        this.colors = [];
        this.historyCenterParameters = this.changesHistoryService.getHistoryCenterParameters('accessibility');
        this.showHistory = this.activeFeatureService.isFeatureActivePure('history_center_all');
        this.domain = this.sessionService.domain;
        this.accessibilityAbbreviations = [
            {
                abbr: 'A',
                guidelineId: 'QUALWEB-A',
                iconClass: 'fa-mon-wcag-a',
                tooltipTxt: this.translateService.getString('Level A Issues'),
            },
            {
                abbr: 'AA',
                guidelineId: 'QUALWEB-AA',
                iconClass: 'fa-mon-wcag-aa',
                tooltipTxt: this.translateService.getString('Level AA Issues'),
            },
            {
                abbr: 'A',
                guidelineId: 'WCAG2-A',
                iconClass: 'fa-mon-wcag-a',
                tooltipTxt: this.translateService.getString('Level A Issues'),
            },
            {
                abbr: 'AA',
                guidelineId: 'WCAG2-AA',
                iconClass: 'fa-mon-wcag-aa',
                tooltipTxt: this.translateService.getString('Level AA Issues'),
            },
            {
                abbr: 'AAA',
                guidelineId: 'WCAG2-AAA',
                iconClass: 'fa-mon-wcag-aaa',
                tooltipTxt: this.translateService.getString('Level AAA Issues'),
            },
            {
                abbr: 'A',
                guidelineId: 'WCAG21-A',
                iconClass: 'fa-mon-wcag-a',
                tooltipTxt: this.translateService.getString('Level A Issues'),
            },
            {
                abbr: 'AA',
                guidelineId: 'WCAG21-AA',
                iconClass: 'fa-mon-wcag-aa',
                tooltipTxt: this.translateService.getString('Level AA Issues'),
            },
            {
                abbr: 'AAA',
                guidelineId: 'WCAG21-AAA',
                iconClass: 'fa-mon-wcag-aaa',
                tooltipTxt: this.translateService.getString('Level AAA Issues'),
            },
        ];

        const promises = [this.getSummary(), this.getChecks()];
        Promise.all(promises).then((values: any[]) => {
            this.summary = values[0];
            this.checks = values[1];
            this.setupLineDoughnutChart();
            this.loading = false;
        }).catch(() => {
            this.loading = false;
        });
    }

    private getSummary (): Promise<any> {
        return this.accessibilityRepoService.getSummary();
    }

    private getChecks (): Promise<any> {
        return this.accessibilityChecksRepoService.getAll();
    }

    private setupLineDoughnutChart (): void {
        const service = this.accessibilityDoughnutChartService.init(this.summary, this.checks);
        const percentage = service.getPercentageFromResultType(undefined, undefined);
        this.colors = service.getColors();
        this.data = service.getData(undefined, undefined);
        this.options = {
            centerText: {
                percentageLabel: this.pipeService.get('monNumber')(percentage / 100, '0.[00] %'),
                totalLabel: this.translateService.getString('Overall compliance'),
                subLabel: this.translateService.getString('achieved'),
                totalFontSize: 150,
                subFontSize: 150,
            },
            cutoutPercentage: 80,
            tooltips: {
                enabled: false,
                custom: function (tooltip: any) {
                    if (!tooltip) {
                        return;
                    }

                    if (tooltip.x < 5) {
                        tooltip.x = 5;
                    }
                },
                callbacks: {
                    label: (tooltipItem: any, data: any) => {
                        return this.pipeService.get('monNumber')(data.datasets[0].data[tooltipItem.index]);
                    },
                },
            },
            hover: {
                mode: null,
            },
        };
    }

    getChecksCountByAbbreviation (abbreviation: string): number {
        const params: any = {
            only_errors: true,
            abbreviations: [abbreviation],
        };
        const checks = this.complianceAccessibilityService.filterAccessibilityChecksByResultTypes(this.checks, params);
        return checks.length;
    }
}
